import React, { useState } from 'react'
import Header from './header'
import Footer from './footer'
import {Outlet, useLocation} from 'react-router-dom'
import Modal from 'react-modal';
import { getApi } from '../services/apiServices';
import { toast } from "react-toastify";
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import FloatingGetApp from '../Components/FloatingGetApp';
import '../styles/r4l-tokens.css';
import '../styles/r4l-shell.css';
import '../styles/r4l-pages.css';

function Layout({Children}) {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const location = useLocation();
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [isShowingChildDevices, setIsShowingChildDevices] = useState(false);
  const [deviceNavigationPath, setDeviceNavigationPath] = useState([]);
  const [compatibilityData, setCompatibilityData] = useState([]);
  const [compatibilityLoading, setCompatibilityLoading] = useState(false);

  const isDestinationPage = location.pathname === '/destination';

  // Language mapping function to convert app language codes to API language codes
  const getApiLanguageCode = (appLanguage) => {
    const languageMap = {
      'ar': 'ar',     // Arabic
      'da': 'da',     // Danish
      'de': 'de',     // German
      'en': 'en',     // English
      'es': 'es',     // Spanish
      'fr': 'fr',     // French
      'he': 'he',     // Hebrew
      'it': 'it',     // Italian
      'ja': 'ja',     // Japanese
      'ko': 'ko',     // Korean
      'nl': 'nl',     // Dutch
      'no': 'no',     // Norwegian
      'pl': 'pl',     // Polish
      'pt': 'pt',     // Portuguese
      'ru': 'ru',     // Russian
      'sv': 'sv',     // Swedish
      'tr': 'tr',     // Turkish
      'zh': 'zh_CN'   // Chinese (Simplified)
    };
    return languageMap[appLanguage] || 'en'; // Default to English if not found
  };

  const openDeviceModal = async () => {
    setShowDeviceModal(true);
    setSelectedDevice(null);
    setIsShowingChildDevices(false);
    setDeviceNavigationPath([]);
    await fetchCompatibilityData();
  };
  // Function to close modal
  const closeDeviceModal = () => {
    setShowDeviceModal(false);
    setSelectedDevice(null);
    setIsShowingChildDevices(false);
    setDeviceNavigationPath([]);
  };

  const fetchCompatibilityData = async () => {
    try {
      setCompatibilityLoading(true);
      const apiLanguage = getApiLanguageCode(currentLanguage);
      const response = await getApi(`/api/compatibilities?lang=${apiLanguage}`);
      if (response?.status) {
        setCompatibilityData(response?.data || []);
      } else {
        console.error('Failed to fetch compatibility data:', response?.msg);
        toast.error('Failed to load device compatibility data');
      }
    } catch (error) {
      console.error('Error fetching compatibility data:', error);
      toast.error('Failed to load device compatibility data');
    } finally {
      setCompatibilityLoading(false);
    }
  };

  const fetchChildDevices = async (parentId) => {
    try {
      setCompatibilityLoading(true);
      const apiLanguage = getApiLanguageCode(currentLanguage);
      const response = await getApi(`/api/compatibilities/by-parent?parent_id=${parentId}&lang=${apiLanguage}`);
      if (response?.status) {
        setCompatibilityData(response?.data || []);
        setIsShowingChildDevices(true);
      } else {
        console.error('Failed to fetch child devices:', response?.msg);
        toast.error('Failed to load child devices');
      }
    } catch (error) {
      console.error('Error fetching child devices:', error);
      toast.error('Failed to load child devices');
    } finally {
      setCompatibilityLoading(false);
    }
  };

  const handleDeviceClick = async (device) => {
    await fetchChildDevices(device.id);
    setDeviceNavigationPath(prev => [...prev, device]);
    setSelectedDevice(device);
  };

  const handleBackToParent = async () => {
    if (deviceNavigationPath.length > 1) {
      const newPath = deviceNavigationPath.slice(0, -1);
      const previousDevice = newPath[newPath.length - 1];
      setDeviceNavigationPath(newPath);
      setSelectedDevice(previousDevice);
      await fetchChildDevices(previousDevice.id);
    } else {
      setSelectedDevice(null);
      setIsShowingChildDevices(false);
      setDeviceNavigationPath([]);
      await fetchCompatibilityData();
    }
  };

  const handleDeviceSelection = async (device) => {
    try {
      setCompatibilityLoading(true);
      const apiLanguage = getApiLanguageCode(currentLanguage);
      const response = await getApi(`/api/compatibilities/by-parent?parent_id=${device.id}&lang=${apiLanguage}`);
      if (response?.status && response?.data && response.data.length > 0) {
        const firstItem = response.data[0];
        if (firstItem.description && firstItem.name === null) {
          setCompatibilityData([firstItem]);
          setIsShowingChildDevices(true);
          setDeviceNavigationPath(prev => [...prev, device]);
          setSelectedDevice(device);
        } else {
          await handleDeviceClick(device);
        }
      } else {
        closeDeviceModal();
      }
    } catch (error) {
      console.error('Error checking device children:', error);
      closeDeviceModal();
    } finally {
      setCompatibilityLoading(false);
    }
  };

  const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      border: 'none',
      borderRadius: '10px',
      padding: '0',
      maxWidth: '600px',
      width: '90%',
      backgroundColor: 'transparent'
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000
    }
  };

  return (
    <>
      <Header/>
      {/* r4l-page scopes the redesign styles for pages still using legacy markup */}
      <main className="r4l-page">
        <Outlet context={{ openDeviceModal }} />
        {Children}
      </main>
      <Footer className={isDestinationPage ? 'footer-destination' : ''} openDeviceModal={openDeviceModal} />
      <Modal 
        isOpen={showDeviceModal}
        onRequestClose={closeDeviceModal}
        style={customStyles}
        contentLabel="Device Compatibility"
        portalClassName="device-compatibility-modal"
      >
        <div className="paymentsuccess compatibilitymodal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {isShowingChildDevices && selectedDevice && compatibilityData.length > 0 && compatibilityData[0].name !== null
                  ? t('deviceCompatibility.chooseDevice', { deviceName: selectedDevice?.name })
                  : isShowingChildDevices && compatibilityData.length > 0 && compatibilityData[0].name === null
                  ? ''
                  : t('deviceCompatibility.checkCompatibility')
                }
              </h3>
              <button type="button" onClick={closeDeviceModal} className="close" data-dismiss="modal" aria-label="Close">
                <img src={window.getPublicUrl('images/close.svg')} alt="Close" />
              </button>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-sm-12"> 
                  {!isShowingChildDevices && (
                    <p className='mb-3'>{t('deviceCompatibility.deviceNotListed')}</p>
                  )}
                  <div className="device-compatibility-content">
                    {compatibilityLoading ? (
                      <div className="loader-overlay">
                        <span className="loader"></span>
                      </div>
                    ) : compatibilityData.length === 0 ? (
                      <div className="text-center">
                        <h4>{t('deviceCompatibility.noDataAvailable')}</h4>
                      </div>
                    ) : (
                      <div className="device-buttons-container mt-3">
                        {compatibilityData.map((device, index) => (
                          device.name === null && device.description ? (
                            <div key={device.id || index} className="compatibility-description" style={{
                              padding: '20px',
                              backgroundColor: '#f8f9fa',
                              borderRadius: '8px',
                              border: '1px solid #e9ecef',
                              marginBottom: '20px'
                            }}>
                              <div style={{
                                whiteSpace: 'pre-line',
                                lineHeight: '1.6',
                                color: '#333',
                                fontSize: '14px'
                              }}>
                                {device.description}
                              </div>
                            </div>
                          ) : (
                            <button
                              key={device.id || index}
                              className="device-compatibility-btn"
                              onClick={() => {
                                handleDeviceSelection(device);
                              }}
                              style={{
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#f8f9fa';
                                e.target.style.borderColor = '#ddd';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = '#fff';
                                e.target.style.borderColor = '#ddd';
                              }}
                            >
                              {device.name}
                              <span style={{
                                position: 'absolute',
                                right: '14px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#ddd',
                                fontSize: '12px'
                              }}>
                                →
                              </span>
                            </button>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer border-0 pt-0 justify-content-start">
              {isShowingChildDevices && (
                <button
                  type="button"
                  className="btn btn-secondary me-2 "
                  onClick={handleBackToParent}
                  style={{
                    backgroundColor: 'transparent',
                    borderColor: '#6c757d',
                    color: '#000',
                    padding: '8px 20px',
                    borderRadius: '23px',
                    border: '1px solid #6c757d',
                    cursor: 'pointer'
                  }}
                >
                  ← {t('deviceCompatibility.back')}
                </button>
              )}
              <button
                type="button"
                className="btn view-all get-sim m-0"
                onClick={closeDeviceModal}
                style={{
                    width: '54px',
                    padding: '9px',
                    fontSize: '14px'
                  }}
              >
                {t('deviceCompatibility.ok')}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Floating Get App Button */}
      <FloatingGetApp />
    </>
  )
}

export default Layout